import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import {DateFilters, dateFilterValues, FilterEvent, FilterProperties, Filters, TypeFilters} from './filters.model';
import {addDays, addMonths, addWeeks, addYears, isAfter, subDays, subMonths, subWeeks, subYears} from 'date-fns/esm';
import {ElementFilters} from '../../element-utils/element-filters/element-filters';
import {Observable} from 'rxjs';
import { map } from 'rxjs/operators';
import { NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'qm-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  providers: [NgbDropdownConfig],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FiltersComponent {
  public selectedFilters: ElementFilters = {
    date: 'All',
    text: '',
    type: 'All'
  };
  public dateTypes = dateFilterValues;
  public dateFilter: DateFilters = {
    startDate: new Date(),
    endDate: new Date(),
    selected_type: 'All'
  };
  public startDate: Date;
  public endDate: Date;
  public model: string;
  @Output() filtersChanged: EventEmitter<FilterEvent> = new EventEmitter<any>();
  @Input() range: boolean = false;
  @Input() filterDefinitions: Filters[];
  @Input() typeAheadDefinitions: Filters;
  @Input() typeFilters: TypeFilters;
  @Input() position: 'left' | 'center' = 'center';
  @Input() placeholder?: string;

  public search = (text$: Observable<string>) =>
    text$.pipe(map(term => {
      if (term === '') {
        return [];
      }

      // simple substring filtering
      const upperTerm = term.toUpperCase();
      const startsWithTerm = (str: string, term: string) => str.toUpperCase().startsWith(term.toUpperCase());
      let matches = this.typeAheadDefinitions.filter_values
        .filter(v => v.name.toUpperCase().includes(upperTerm))
        // Sort results
        .sort((a, b) => {
          // Match being in the start more -> show on top
          const aPrio = (startsWithTerm(a.value, term) || startsWithTerm(a.name, term)) ? 1 : 0;
          const bPrio = (startsWithTerm(b.value, term) || startsWithTerm(b.name, term)) ? 1 : 0;
          return bPrio - aPrio;
        });

      // Exact values are shown always on the top
      const valueMatchIndex = matches.findIndex(v => v.value === upperTerm);
      if (valueMatchIndex !== -1) {
        const valueMatch = matches[valueMatchIndex];
        matches.splice(valueMatchIndex, 1)
        matches.unshift(valueMatch);
      }

      return matches.slice(0, 10);
    }));

  public formatter = (x: {name: string}) => x.name;

  constructor(config: NgbDropdownConfig, private changeDetector: ChangeDetectorRef) {
    config.autoClose = 'outside';
    this.startDate = new Date();
    this.endDate = new Date();
    this.startDate.setHours(0, 0, 0, 0);
    this.endDate.setHours(23, 59, 59, 999);
  }

  public typeAheadEvent($event) {
    this.typeAheadDefinitions.filter_values.forEach((o: any) => {
      if (o.value === $event.item.value) {
        o.boolean = !o.boolean;
      }
    });
    // cancelling the event will allow clearing the input;
    // otherwise the value is asynchronously inserted
    $event.preventDefault();
    this.model = '';
    this.selectedFilterChanged();
  }

  public selectedFilterChanged() {
    this.dateFilter.startDate = this.startDate;
    this.dateFilter.endDate = this.endDate;
    this.filtersChanged.emit(new FilterEvent(this.dateFilter, this.typeFilters, this.filterDefinitions, this.typeAheadDefinitions));
    this.changeDetector.markForCheck();
  }

  public timeTravels(direction: 'next' | 'previous' | ''): void {
    if (direction === 'next') {
      this.timeTravelsNext();
    } else if (direction === 'previous') {
      this.timeTravelsPrevious();
    } else {
      this.timeTravelsNone();
    }
  }

  private timeTravelsNext(): void {
    this.startDate = new Date(this.endDate.getTime());
    switch (this.dateFilter.selected_type) {
      case 'Day':
        this.endDate = addDays(this.startDate, 1);
        break;
      case 'Week':
        this.endDate = addWeeks(this.startDate, 1);
        break;
      case 'Month':
        this.endDate = addMonths(this.startDate, 1);
        break;
      case 'Year':
        this.endDate = addYears(this.startDate, 1);
        break;
    }
    if (this.futureCheck()) {
      this.selectedFilterChanged();
    }
  }

  private timeTravelsPrevious(): void {
    this.endDate = new Date(this.startDate);
    switch (this.dateFilter.selected_type) {
      case 'Day':
        this.startDate = subDays(this.endDate, 1);
        break;
      case 'Week':
        this.startDate = subWeeks(this.endDate, 1);
        break;
      case 'Month':
        this.startDate = subMonths(this.endDate, 1);
        break;
      case 'Year':
        this.startDate = subYears(this.endDate, 1);
        break;
    }
    this.selectedFilterChanged();
  }

  private timeTravelsNone(): void {
    switch (this.dateFilter.selected_type) {
      case 'Day':
        this.startDate = subDays(this.endDate, 1);
        break;
      case 'Week':
        this.startDate = subWeeks(this.endDate, 1);
        break;
      case 'Month':
        this.startDate = subMonths(this.endDate, 1);
        break;
      case 'Year':
        this.startDate = subYears(this.endDate, 1);
        break;
    }
    this.selectedFilterChanged();
  }

  private futureCheck(): boolean {
    const today = new Date();
    if (isAfter(this.endDate, today)) {
      this.startDate = new Date(today.getTime());
      this.timeTravelsPrevious();
      return false;
    }
    return true;
  }

  public dropDownType(index: number, selected: FilterProperties) {
    console.log('hmm filter', selected);
    this.filterDefinitions[index].filter_values.forEach((o: FilterProperties) => {
      if (o.name !== selected.name) {
        o.boolean = false;
      }
    });
    this.selectedFilterChanged();
  }

  public getActiveFilterCount(filters: FilterProperties[]): string {
    if (filters) {
      const l = filters.filter(o => {
        return o.boolean;
      }).length;
      return '(' + l + '/' + filters.length + ')';
    }
  }
}
